/**
 * Open a document the browser can render, without ever handing it a URL that
 * would need no session.
 *
 * The bytes for these documents are behind authenticated routes -- nginx denies
 * `/policy/media/` outright -- so a preview cannot be an `href`. It has to be a
 * fetch through the API client, then a `blob:` URL. That is easy to get subtly
 * wrong in three ways this module exists to get right ONCE, rather than in each
 * panel that wants a View button. #243 learned all three on policy documents
 * (`PolicyDetailPage.handleViewDocument`); B-03 needed the same thing on the
 * contact KYC panel, and the channel KYC panel is the next caller in line once
 * the lane that owns `apps/brokers/views.py` teaches it `?disposition=inline`.
 * Extracted rather than copied for exactly that reason.
 *
 * 1. THE TAB IS OPENED SYNCHRONOUSLY, before the fetch is awaited. A
 *    `window.open` in an async continuation is outside the user-gesture window,
 *    so browsers block it silently and View appears to do nothing at all --
 *    "success with nothing behind it", this platform's signature defect. jsdom
 *    has no popup blocker, so no test can catch a regression here by accident;
 *    the ordering is asserted deliberately instead.
 * 2. THE TYPE ALLOWLIST IS RE-APPLIED to the blob actually received. A `blob:`
 *    URL inherits the creating document's origin, so pointing a tab at an HTML
 *    or SVG blob would execute an uploader's markup on our own domain, against
 *    the session viewing it. The server already refuses to serve those inline
 *    (`backend/apps/core/file_disposition.py`); this is the second lock on the
 *    same door, and the set below is deliberately identical to that one.
 * 3. THE OBJECT URL IS REVOKED. Not immediately -- the new tab has not loaded
 *    it yet, and revoking early leaves a blank tab -- but on a timer, because
 *    an un-revoked URL pins the whole file in memory for the life of the page.
 *
 * Anything that cannot be previewed still DOWNLOADS, exactly as it did before.
 * Failing safe, not open, and never failing silently: every outcome below is
 * one the caller can put on screen.
 */

/** The only types handed to a tab on our own origin. Mirrors
 *  `INLINE_SAFE_TYPES` in `backend/apps/core/file_disposition.py`. */
export const PREVIEWABLE_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);

/** How long the tab gets to load the blob before the URL is revoked. Matches
 *  the policy-document preview's own window. */
const REVOKE_AFTER_MS = 60000;

export type PreviewOutcome =
  /** It is on screen in a new tab. */
  | 'previewed'
  /** The browser cannot be trusted to render this type, so it was saved. */
  | 'downloaded-unsupported-type'
  /** A popup blocker ate the tab, so it was saved instead of vanishing. */
  | 'downloaded-popup-blocked';

export interface PreviewRequest {
  /** Fetch the bytes through an AUTHENTICATED service. Never a bare URL. */
  fetchBlob: () => Promise<Blob>;
  /** Save the file instead, for the outcomes where a preview is refused. */
  download: () => Promise<void>;
}

/**
 * Preview a document in a new tab, falling back to a download.
 *
 * Throws whatever `fetchBlob` throws (and closes the tab first, so a failed
 * fetch never leaves a blank one standing) -- the caller decides how to say
 * that the document could not be opened, because the panels already have a
 * place to say it.
 */
export async function previewInNewTab({ fetchBlob, download }: PreviewRequest): Promise<PreviewOutcome> {
  // Synchronous, before any await. See (1) above.
  const tab = typeof window.open === 'function' ? window.open('', '_blank') : null;
  if (tab) {
    // Severing the opener costs nothing and keeps the new tab independent.
    // `noopener` is NOT passed to window.open: per spec that makes it return
    // null, and the handle is exactly what we need.
    try {
      tab.opener = null;
    } catch {
      // Some browsers make `opener` read-only; harmless either way.
    }
  }

  let blob: Blob;
  try {
    blob = await fetchBlob();
  } catch (error) {
    tab?.close();
    throw error;
  }

  if (!tab) {
    await download();
    return 'downloaded-popup-blocked';
  }

  const type = (blob.type || '').split(';')[0].trim().toLowerCase();
  if (!PREVIEWABLE_TYPES.has(type)) {
    tab.close();
    await download();
    return 'downloaded-unsupported-type';
  }

  const objectUrl = URL.createObjectURL(blob);
  tab.location.href = objectUrl;
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), REVOKE_AFTER_MS);
  return 'previewed';
}

/** What to tell the viewer about an outcome, or '' when it just worked. */
export function previewOutcomeMessage(outcome: PreviewOutcome): string {
  switch (outcome) {
    case 'downloaded-unsupported-type':
      return 'This file type cannot be shown in the browser, so we downloaded it instead.';
    case 'downloaded-popup-blocked':
      return 'Your browser blocked the preview tab, so we downloaded it instead. Allow pop-ups for this site to view documents here.';
    default:
      return '';
  }
}
